// «Las acciones de un agente», definidas UNA vez (05-11, auditoría final: flujo.md #3, #4 y #10). Antes cada superficie
// tenía las suyas: la fila de la lista sabía «Sí» y «Cerrar», el chat del mismo agente no; el menú de la fila tenía «Abrir»
// y «Aparcar», el de la mesa además «Renombrar», «Mover a» y «Terminar». Pau veía un botón en un sitio y lo buscaba en vano
// en el otro. Aquí, PURO:
//   - `estadoDelAgente`: su grupo (LA clasificación de situacion.ts) con el mismo contexto que la lista (Santi, su subida,
//     la hora), si duerme y si se le puede ofrecer aparcar. Lo usan la fila, el riel, el chat, la ficha y la mesa.
//   - `accionUrgente`: la UNA acción que decide (Revisar una subida · Sí al permiso sencillo · Cerrar lo terminado), la
//     misma en la fila, en la línea de encima de la caja del chat y en el menú de la mesa.
//   - `opcionesDelAgente`: el menú (Abrir, Renombrar, Mover a, Aparcar/Despertar, Terminar), el mismo en la fila y en la
//     mesa; el ⋮ del chat lo usa sin «Abrir» (ya está abierto) ni «Mover a» (no se ve la oficina).
import type { ContratadoDTO } from '../compartido/vocabulario';
import type { Aparcado } from '../compartido/aparcar';
import { aparcadoDe, grupoDelContratado, puedeAparcarse, type Grupo, type SantiDelAgente } from './situacion';

/** La acción que decide, si la hay. `revisar` lleva la subida; `si`, el permiso sencillo del servidor. */
export type AccionUrgente<S extends { creadoEn: string } = { creadoEn: string }> =
  | { tipo: 'revisar'; subida: S }
  | { tipo: 'si'; permisoId: string }
  | { tipo: 'cerrar' }
  | null;

/**
 * La acción urgente de un agente en su grupo: una subida suya espera tu Allow → «Revisar»; el permiso sencillo («subir a
 * pruebas») → «Sí»; ha terminado → «Cerrar». Las preguntas no llevan botón: se contestan escribiendo.
 */
export function accionUrgente<S extends { creadoEn: string }>(c: Pick<ContratadoDTO, 'permisoRapido'>, grupo: Grupo, subida: S | null | undefined): AccionUrgente<S> {
  if (subida) return { tipo: 'revisar', subida };
  if (grupo === 'autorizacion' && c.permisoRapido) return { tipo: 'si', permisoId: c.permisoRapido.id };
  if (grupo === 'terminado') return { tipo: 'cerrar' };
  return null;
}

export interface EstadoAgente<S extends { creadoEn: string } = { creadoEn: string }> {
  grupo: Grupo;
  /** Duerme ahora (y desde cuándo); null = despierto. */
  aparcado: Aparcado | null;
  /** Se le puede ofrecer «Aparcar» (compartido/aparcar.ts `puedeDormir`, con su contexto). */
  puedeAparcar: boolean;
  accion: AccionUrgente<S>;
}

/** Todo lo que las superficies necesitan saber de UN agente, del mismo cálculo que la lista. */
export function estadoDelAgente<S extends { creadoEn: string }>(c: ContratadoDTO, santi: SantiDelAgente | undefined, subida: S | null | undefined, ahora: number): EstadoAgente<S> {
  const ctx = { subida: subida ?? null, ahora };
  const grupo = grupoDelContratado(c, santi, ctx);
  return {
    grupo,
    aparcado: grupo === 'aparcado' ? aparcadoDe(c, santi, ctx) : null,
    puedeAparcar: puedeAparcarse(c, santi, ctx),
    accion: accionUrgente(c, grupo, subida),
  };
}

export type OpcionAgente = 'abrir' | 'renombrar' | 'moverA' | 'aparcar' | 'despertar' | 'terminar';

/**
 * Las opciones del menú de un agente, en su orden. `abrir` y `moverA` solo donde tienen sentido (la fila y la mesa: el
 * chat ya está abierto y desde él no se ve la oficina). Aparcar solo a quien puede dormirse; despertar, al que duerme;
 * terminar, al que sigue activo.
 */
export function opcionesDelAgente(
  c: Pick<ContratadoDTO, 'ciclo'>,
  e: { aparcado: Aparcado | boolean | null; puedeAparcar: boolean },
  donde: { abrir: boolean; moverA: boolean },
): OpcionAgente[] {
  const o: OpcionAgente[] = [];
  if (donde.abrir) o.push('abrir');
  o.push('renombrar');
  if (donde.moverA) o.push('moverA');
  if (e.aparcado) o.push('despertar');
  else if (e.puedeAparcar) o.push('aparcar');
  if (c.ciclo === 'activo') o.push('terminar');
  return o;
}
